import { AppLanguage, detectLanguage } from './language';

const en = {
  'common.loginRequired': 'Please log in to continue.',
  'common.authTokenMissing': 'Authentication token not found.',
  'common.videoIdMissing': 'Video ID not found.',
  'common.imageTypeUnsupported': 'Please upload a JPG, PNG, or WebP image.',
  'common.imageDimensionsUnreadable':
    'Could not read the image dimensions. Try a different image.',
  'common.subscriptionRequired':
    'An active subscription is required to continue.',
  'common.insufficientPixels': 'Insufficient pixels.',
  'editor.stillLoading':
    'Editor is still loading. Please try again in a moment.',
  'publish.inProgress': 'Publish already in progress. Please wait.',
  'scene.selectFirst': 'Please select a scene first.',
  'scene.notFound': 'Scene not found in timeline.',
  'scene.required': 'At least one scene is required.',
  'characterVideo.maxCharactersOne': 'You can select up to 1 character.',
  'characterVideo.maxCharactersMany':
    'You can select up to {count} characters.',
  'characterVideo.characterNoImage':
    'This character has no reference image. Add one in Character Studio.',
  'characterVideo.characterImageRequired':
    'Select or upload a character image first.',
  'characterVideo.describeCharacter':
    'Describe how the character image should become a video.',
  'characterVideo.scriptGenerated':
    'Script generated. Review it below, then generate the video.',
  'characterVideo.scriptPixels':
    'This script needs {required} pixels, you have {available}.',
  'characterVideo.scriptFailed': 'Failed to generate script.',
  'characterVideo.scriptRequired':
    'Generate a script first before creating the video.',
  'characterVideo.animationReady':
    'Animation ready — hit Save & Add to Timeline to keep it, otherwise it will be discarded.',
  'characterVideo.videoReady':
    'Video ready — hit Save & Add to Timeline to keep it, otherwise it will be discarded.',
  'characterVideo.videoPixels':
    'This video needs {required} pixels, you have {available}.',
  'characterVideo.videoFailed': 'Failed to generate video.',
  'characterVideo.enhanced': 'Enhanced to 1080p.',
  'characterVideo.enhancePixels':
    'This enhancement needs {required} pixels, you have {available}.',
  'characterVideo.enhanceFailed': 'Failed to enhance video.',
  'characterVideo.timelineVideoRequired':
    'Generate a video before adding it to the timeline.',
  'characterVideo.trimTooShort': 'Select at least 0.1 seconds to save.',
  'characterVideo.saveFailed': 'Failed to save the video. Please try again.',
  'characterVideo.discardVideoConfirm':
    'This generated video will be lost forever if you continue without saving it to the timeline. Continue?',
  'environment.deleted': 'Environment deleted.',
  'environment.deleteFailed': 'Failed to delete environment.',
  'environment.maxImages':
    'An environment can have at most {max} reference images.',
  'environment.onlyFirstImages':
    'Only the first {slots} image(s) were added (max {max}).',
  'environment.unsupportedImage':
    '"{file}" is not a supported image (JPG, PNG, WebP).',
  'environment.aspectRatio':
    '"{file}" has an aspect ratio of {ratio}. Reference images must be between {min} and {max}. Crop it and try again.',
  'environment.uploadFailed': 'Failed to upload reference image.',
  'environment.nameRequired': 'Give your environment a name.',
  'environment.imageRequired': 'Upload at least one reference image.',
  'environment.updated': '"{name}" updated.',
  'environment.created': '"{name}" created.',
  'environment.saveFailed': 'Failed to save environment.',
  'navigation.noRecordedAnimation': 'No recorded animation to play.',
  'navigation.characterUpdateUnavailable':
    'Rigged character update handler not available.',
  'navigation.assetsOnlyAudioVideo':
    'Only audio and video files can be uploaded.',
  'navigation.assetsUploadFailed': 'One or more assets failed to upload.',
  'navigation.audioPromptRequired': 'Please enter an audio prompt.',
  'navigation.audioTitleRequired': 'Please enter an audio title.',
  'navigation.audioStyleRequired': 'Please select an audio style.',
  'navigation.audioGenerateFailed': 'Failed to generate audio.',
  'navigation.backgroundPromptRequired': 'Please enter a background prompt.',
  'navigation.backgroundGenerateFailed': 'Failed to generate background.',
  'profile.firstNameRequired': 'First name cannot be empty.',
  'profile.noChanges': 'No changes to save.',
  'profile.updated': 'Profile updated.',
  'profile.updateFailed': 'Failed to update profile.',
  'profile.passwordChanged': 'Password changed.',
  'profile.passwordChangeFailed': 'Failed to change password.',
  'home.audioAddFailed': 'Failed to add audio to timeline.',
  'home.uploadedVideoAddFailed': 'Failed to add uploaded video to timeline.',
  'home.backgroundGenerated': 'Background generated successfully.',
  'home.backgroundGenerateFailed': 'Failed to generate image.',
  'home.noGeneratedAudio': 'No generated audio to save.',
  'home.audioSaveStorageFailed': 'Failed to save generated audio to storage.',
  'home.audioAdded': 'Generated audio added to timeline.',
  'home.audioSaveFailed': 'Failed to save generated audio.',
  'home.videoSaveFailed':
    'Failed to save the generated video. Please try again.',
  'billing.cancellationScheduled':
    'Cancellation scheduled — active until {date}',
} as const;

export type ToastMessageKey = keyof typeof en;
export type ToastMessageVars = Record<string, string | number>;

const zh: Record<ToastMessageKey, string> = {
  'common.loginRequired': '请先登录后继续。',
  'common.authTokenMissing': '未找到身份验证令牌。',
  'common.videoIdMissing': '未找到视频 ID。',
  'common.imageTypeUnsupported': '请上传 JPG、PNG 或 WebP 图片。',
  'common.imageDimensionsUnreadable': '无法读取图片尺寸，请尝试其他图片。',
  'common.subscriptionRequired': '需要有效订阅才能继续。',
  'common.insufficientPixels': '像素不足。',
  'editor.stillLoading': '编辑器仍在加载，请稍后再试。',
  'publish.inProgress': '发布已在进行中，请稍候。',
  'scene.selectFirst': '请先选择一个场景。',
  'scene.notFound': '时间线中未找到该场景。',
  'scene.required': '至少需要一个场景。',
  'characterVideo.maxCharactersOne': '最多只能选择 1 个角色。',
  'characterVideo.maxCharactersMany': '最多只能选择 {count} 个角色。',
  'characterVideo.characterNoImage':
    '此角色没有参考图片，请在角色工作室中添加。',
  'characterVideo.characterImageRequired': '请先选择或上传角色图片。',
  'characterVideo.describeCharacter': '请描述角色图片应如何转换为视频。',
  'characterVideo.scriptGenerated': '脚本已生成。请在下方查看，然后生成视频。',
  'characterVideo.scriptPixels':
    '此脚本需要 {required} 像素，你当前有 {available}。',
  'characterVideo.scriptFailed': '生成脚本失败。',
  'characterVideo.scriptRequired': '请先生成脚本，再创建视频。',
  'characterVideo.animationReady':
    '动画已就绪 — 点击“保存并添加到时间线”以保留，否则将被丢弃。',
  'characterVideo.videoReady':
    '视频已就绪 — 点击“保存并添加到时间线”以保留，否则将被丢弃。',
  'characterVideo.videoPixels':
    '此视频需要 {required} 像素，你当前有 {available}。',
  'characterVideo.videoFailed': '生成视频失败。',
  'characterVideo.enhanced': '已增强至 1080p。',
  'characterVideo.enhancePixels':
    '此增强需要 {required} 像素，你当前有 {available}。',
  'characterVideo.enhanceFailed': '增强视频失败。',
  'characterVideo.timelineVideoRequired': '请先生成视频，再添加到时间线。',
  'characterVideo.trimTooShort': '请至少选择 0.1 秒后再保存。',
  'characterVideo.saveFailed': '保存视频失败，请重试。',
  'characterVideo.discardVideoConfirm':
    '此生成的视频将永久丢失，除非先保存到时间线。是否继续？',
  'environment.deleted': '环境已删除。',
  'environment.deleteFailed': '删除环境失败。',
  'environment.maxImages': '一个环境最多只能有 {max} 张参考图片。',
  'environment.onlyFirstImages': '仅添加了前 {slots} 张图片（最多 {max} 张）。',
  'environment.unsupportedImage': '“{file}”不是支持的图片（JPG、PNG、WebP）。',
  'environment.aspectRatio':
    '“{file}”的宽高比为 {ratio}。参考图片的宽高比必须在 {min} 到 {max} 之间。请裁剪后重试。',
  'environment.uploadFailed': '上传参考图片失败。',
  'environment.nameRequired': '请为环境命名。',
  'environment.imageRequired': '请至少上传一张参考图片。',
  'environment.updated': '“{name}”已更新。',
  'environment.created': '“{name}”已创建。',
  'environment.saveFailed': '保存环境失败。',
  'navigation.noRecordedAnimation': '没有可播放的录制动画。',
  'navigation.characterUpdateUnavailable': '角色更新功能暂不可用。',
  'navigation.assetsOnlyAudioVideo': '只能上传音频和视频文件。',
  'navigation.assetsUploadFailed': '一个或多个素材上传失败。',
  'navigation.audioPromptRequired': '请输入音频提示词。',
  'navigation.audioTitleRequired': '请输入音频标题。',
  'navigation.audioStyleRequired': '请选择音频风格。',
  'navigation.audioGenerateFailed': '生成音频失败。',
  'navigation.backgroundPromptRequired': '请输入背景提示词。',
  'navigation.backgroundGenerateFailed': '生成背景失败。',
  'profile.firstNameRequired': '名字不能为空。',
  'profile.noChanges': '没有需要保存的更改。',
  'profile.updated': '个人资料已更新。',
  'profile.updateFailed': '更新个人资料失败。',
  'profile.passwordChanged': '密码已更改。',
  'profile.passwordChangeFailed': '更改密码失败。',
  'home.audioAddFailed': '将音频添加到时间线失败。',
  'home.uploadedVideoAddFailed': '将上传的视频添加到时间线失败。',
  'home.backgroundGenerated': '背景生成成功。',
  'home.backgroundGenerateFailed': '生成图片失败。',
  'home.noGeneratedAudio': '没有可保存的生成音频。',
  'home.audioSaveStorageFailed': '将生成音频保存到存储失败。',
  'home.audioAdded': '生成音频已添加到时间线。',
  'home.audioSaveFailed': '保存生成音频失败。',
  'home.videoSaveFailed': '保存生成的视频失败，请重试。',
  'billing.cancellationScheduled': '已安排取消 — 有效期至 {date}',
};

const es: Record<ToastMessageKey, string> = {
  'common.loginRequired': 'Inicia sesión para continuar.',
  'common.authTokenMissing': 'No se encontró el token de autenticación.',
  'common.videoIdMissing': 'No se encontró el ID del video.',
  'common.imageTypeUnsupported': 'Sube una imagen JPG, PNG o WebP.',
  'common.imageDimensionsUnreadable':
    'No se pudieron leer las dimensiones de la imagen. Prueba con otra imagen.',
  'common.subscriptionRequired':
    'Se requiere una suscripción activa para continuar.',
  'common.insufficientPixels': 'Pixeles insuficientes.',
  'editor.stillLoading':
    'El editor aún se está cargando. Inténtalo de nuevo en un momento.',
  'publish.inProgress': 'La publicación ya está en curso. Espera.',
  'scene.selectFirst': 'Selecciona primero una escena.',
  'scene.notFound': 'No se encontró la escena en la línea de tiempo.',
  'scene.required': 'Se requiere al menos una escena.',
  'characterVideo.maxCharactersOne': 'Solo puedes seleccionar 1 personaje.',
  'characterVideo.maxCharactersMany':
    'Solo puedes seleccionar hasta {count} personajes.',
  'characterVideo.characterNoImage':
    'Este personaje no tiene imagen de referencia. Añade una en Character Studio.',
  'characterVideo.characterImageRequired':
    'Selecciona o sube primero una imagen de personaje.',
  'characterVideo.describeCharacter':
    'Describe cómo la imagen del personaje debe convertirse en video.',
  'characterVideo.scriptGenerated':
    'Guion generado. Revísalo abajo y luego genera el video.',
  'characterVideo.scriptPixels':
    'Este guion necesita {required} pixeles, tienes {available}.',
  'characterVideo.scriptFailed': 'No se pudo generar el guion.',
  'characterVideo.scriptRequired':
    'Genera primero un guion antes de crear el video.',
  'characterVideo.animationReady':
    'Animación lista — pulsa Guardar y añadir a la línea de tiempo para conservarla; de lo contrario se descartará.',
  'characterVideo.videoReady':
    'Video listo — pulsa Guardar y añadir a la línea de tiempo para conservarlo; de lo contrario se descartará.',
  'characterVideo.videoPixels':
    'Este video necesita {required} pixeles, tienes {available}.',
  'characterVideo.videoFailed': 'No se pudo generar el video.',
  'characterVideo.enhanced': 'Mejorado a 1080p.',
  'characterVideo.enhancePixels':
    'Esta mejora necesita {required} pixeles, tienes {available}.',
  'characterVideo.enhanceFailed': 'No se pudo mejorar el video.',
  'characterVideo.timelineVideoRequired':
    'Genera un video antes de añadirlo a la línea de tiempo.',
  'characterVideo.trimTooShort':
    'Selecciona al menos 0.1 segundos para guardar.',
  'characterVideo.saveFailed':
    'No se pudo guardar el video. Inténtalo de nuevo.',
  'characterVideo.discardVideoConfirm':
    'Este video generado se perderá para siempre si continúas sin guardarlo en la línea de tiempo. ¿Continuar?',
  'environment.deleted': 'Entorno eliminado.',
  'environment.deleteFailed': 'No se pudo eliminar el entorno.',
  'environment.maxImages':
    'Un entorno puede tener como máximo {max} imágenes de referencia.',
  'environment.onlyFirstImages':
    'Solo se añadieron las primeras {slots} imágenes (máx. {max}).',
  'environment.unsupportedImage':
    '"{file}" no es una imagen compatible (JPG, PNG, WebP).',
  'environment.aspectRatio':
    '"{file}" tiene una relación de aspecto de {ratio}. Las imágenes de referencia deben estar entre {min} y {max}. Recórtala e inténtalo de nuevo.',
  'environment.uploadFailed': 'No se pudo subir la imagen de referencia.',
  'environment.nameRequired': 'Ponle un nombre al entorno.',
  'environment.imageRequired': 'Sube al menos una imagen de referencia.',
  'environment.updated': '"{name}" actualizado.',
  'environment.created': '"{name}" creado.',
  'environment.saveFailed': 'No se pudo guardar el entorno.',
  'navigation.noRecordedAnimation': 'No hay animación grabada para reproducir.',
  'navigation.characterUpdateUnavailable':
    'El actualizador de personajes no está disponible.',
  'navigation.assetsOnlyAudioVideo':
    'Solo se pueden subir archivos de audio y video.',
  'navigation.assetsUploadFailed': 'Uno o más recursos no se pudieron subir.',
  'navigation.audioPromptRequired': 'Introduce un prompt de audio.',
  'navigation.audioTitleRequired': 'Introduce un título de audio.',
  'navigation.audioStyleRequired': 'Selecciona un estilo de audio.',
  'navigation.audioGenerateFailed': 'No se pudo generar el audio.',
  'navigation.backgroundPromptRequired': 'Introduce un prompt de fondo.',
  'navigation.backgroundGenerateFailed': 'No se pudo generar el fondo.',
  'profile.firstNameRequired': 'El nombre no puede estar vacío.',
  'profile.noChanges': 'No hay cambios que guardar.',
  'profile.updated': 'Perfil actualizado.',
  'profile.updateFailed': 'No se pudo actualizar el perfil.',
  'profile.passwordChanged': 'Contraseña cambiada.',
  'profile.passwordChangeFailed': 'No se pudo cambiar la contraseña.',
  'home.audioAddFailed': 'No se pudo añadir el audio a la línea de tiempo.',
  'home.uploadedVideoAddFailed':
    'No se pudo añadir el video subido a la línea de tiempo.',
  'home.backgroundGenerated': 'Fondo generado correctamente.',
  'home.backgroundGenerateFailed': 'No se pudo generar la imagen.',
  'home.noGeneratedAudio': 'No hay audio generado para guardar.',
  'home.audioSaveStorageFailed':
    'No se pudo guardar el audio generado en el almacenamiento.',
  'home.audioAdded': 'Audio generado añadido a la línea de tiempo.',
  'home.audioSaveFailed': 'No se pudo guardar el audio generado.',
  'home.videoSaveFailed':
    'No se pudo guardar el video generado. Inténtalo de nuevo.',
  'billing.cancellationScheduled':
    'Cancelación programada — activa hasta {date}',
};

const fr: Record<ToastMessageKey, string> = {
  'common.loginRequired': 'Connectez-vous pour continuer.',
  'common.authTokenMissing': "Jeton d'authentification introuvable.",
  'common.videoIdMissing': 'ID vidéo introuvable.',
  'common.imageTypeUnsupported': 'Importez une image JPG, PNG ou WebP.',
  'common.imageDimensionsUnreadable':
    "Impossible de lire les dimensions de l'image. Essayez une autre image.",
  'common.subscriptionRequired':
    'Un abonnement actif est requis pour continuer.',
  'common.insufficientPixels': 'Pixels insuffisants.',
  'editor.stillLoading':
    "L'éditeur est encore en cours de chargement. Réessayez dans un instant.",
  'publish.inProgress': 'La publication est déjà en cours. Veuillez patienter.',
  'scene.selectFirst': "Sélectionnez d'abord une scène.",
  'scene.notFound': 'Scène introuvable dans la chronologie.',
  'scene.required': 'Au moins une scène est requise.',
  'characterVideo.maxCharactersOne':
    'Vous pouvez sélectionner 1 personnage maximum.',
  'characterVideo.maxCharactersMany':
    'Vous pouvez sélectionner jusqu’à {count} personnages.',
  'characterVideo.characterNoImage':
    "Ce personnage n'a pas d'image de référence. Ajoutez-en une dans Character Studio.",
  'characterVideo.characterImageRequired':
    "Sélectionnez ou importez d'abord une image de personnage.",
  'characterVideo.describeCharacter':
    "Décrivez comment l'image du personnage doit devenir une vidéo.",
  'characterVideo.scriptGenerated':
    'Script généré. Consultez-le ci-dessous, puis générez la vidéo.',
  'characterVideo.scriptPixels':
    'Ce script nécessite {required} pixels, vous en avez {available}.',
  'characterVideo.scriptFailed': 'Impossible de générer le script.',
  'characterVideo.scriptRequired':
    'Générez d’abord un script avant de créer la vidéo.',
  'characterVideo.animationReady':
    'Animation prête — cliquez sur Enregistrer et ajouter à la chronologie pour la conserver, sinon elle sera supprimée.',
  'characterVideo.videoReady':
    'Vidéo prête — cliquez sur Enregistrer et ajouter à la chronologie pour la conserver, sinon elle sera supprimée.',
  'characterVideo.videoPixels':
    'Cette vidéo nécessite {required} pixels, vous en avez {available}.',
  'characterVideo.videoFailed': 'Impossible de générer la vidéo.',
  'characterVideo.enhanced': 'Amélioré en 1080p.',
  'characterVideo.enhancePixels':
    'Cette amélioration nécessite {required} pixels, vous en avez {available}.',
  'characterVideo.enhanceFailed': "Impossible d'améliorer la vidéo.",
  'characterVideo.timelineVideoRequired':
    'Générez une vidéo avant de l’ajouter à la chronologie.',
  'characterVideo.trimTooShort':
    'Sélectionnez au moins 0,1 seconde pour enregistrer.',
  'characterVideo.saveFailed':
    "Impossible d'enregistrer la vidéo. Veuillez réessayer.",
  'characterVideo.discardVideoConfirm':
    'Cette vidéo générée sera définitivement perdue si vous continuez sans l’enregistrer dans la chronologie. Continuer ?',
  'environment.deleted': 'Environnement supprimé.',
  'environment.deleteFailed': "Impossible de supprimer l'environnement.",
  'environment.maxImages':
    'Un environnement peut contenir au maximum {max} images de référence.',
  'environment.onlyFirstImages':
    'Seules les {slots} premières images ont été ajoutées (max. {max}).',
  'environment.unsupportedImage':
    '"{file}" n’est pas une image prise en charge (JPG, PNG, WebP).',
  'environment.aspectRatio':
    '"{file}" a un rapport d’aspect de {ratio}. Les images de référence doivent être comprises entre {min} et {max}. Recadrez-la et réessayez.',
  'environment.uploadFailed': "Impossible d'importer l'image de référence.",
  'environment.nameRequired': "Donnez un nom à l'environnement.",
  'environment.imageRequired': 'Importez au moins une image de référence.',
  'environment.updated': '"{name}" mis à jour.',
  'environment.created': '"{name}" créé.',
  'environment.saveFailed': "Impossible d'enregistrer l'environnement.",
  'navigation.noRecordedAnimation': 'Aucune animation enregistrée à lire.',
  'navigation.characterUpdateUnavailable':
    'Le gestionnaire de mise à jour des personnages est indisponible.',
  'navigation.assetsOnlyAudioVideo':
    'Seuls les fichiers audio et vidéo peuvent être importés.',
  'navigation.assetsUploadFailed':
    'Un ou plusieurs éléments n’ont pas pu être importés.',
  'navigation.audioPromptRequired': 'Saisissez un prompt audio.',
  'navigation.audioTitleRequired': 'Saisissez un titre audio.',
  'navigation.audioStyleRequired': 'Sélectionnez un style audio.',
  'navigation.audioGenerateFailed': "Impossible de générer l'audio.",
  'navigation.backgroundPromptRequired': 'Saisissez un prompt de fond.',
  'navigation.backgroundGenerateFailed': 'Impossible de générer le fond.',
  'profile.firstNameRequired': 'Le prénom ne peut pas être vide.',
  'profile.noChanges': 'Aucune modification à enregistrer.',
  'profile.updated': 'Profil mis à jour.',
  'profile.updateFailed': 'Impossible de mettre à jour le profil.',
  'profile.passwordChanged': 'Mot de passe modifié.',
  'profile.passwordChangeFailed': 'Impossible de modifier le mot de passe.',
  'home.audioAddFailed': "Impossible d'ajouter l'audio à la chronologie.",
  'home.uploadedVideoAddFailed':
    "Impossible d'ajouter la vidéo importée à la chronologie.",
  'home.backgroundGenerated': 'Fond généré avec succès.',
  'home.backgroundGenerateFailed': "Impossible de générer l'image.",
  'home.noGeneratedAudio': 'Aucun audio généré à enregistrer.',
  'home.audioSaveStorageFailed':
    "Impossible d'enregistrer l'audio généré dans le stockage.",
  'home.audioAdded': 'Audio généré ajouté à la chronologie.',
  'home.audioSaveFailed': "Impossible d'enregistrer l'audio généré.",
  'home.videoSaveFailed':
    "Impossible d'enregistrer la vidéo générée. Veuillez réessayer.",
  'billing.cancellationScheduled':
    "Annulation programmée — active jusqu'au {date}",
};

const de: Record<ToastMessageKey, string> = {
  'common.loginRequired': 'Bitte melde dich an, um fortzufahren.',
  'common.authTokenMissing': 'Authentifizierungstoken nicht gefunden.',
  'common.videoIdMissing': 'Video-ID nicht gefunden.',
  'common.imageTypeUnsupported':
    'Bitte lade ein JPG-, PNG- oder WebP-Bild hoch.',
  'common.imageDimensionsUnreadable':
    'Die Bildabmessungen konnten nicht gelesen werden. Versuche ein anderes Bild.',
  'common.subscriptionRequired':
    'Zum Fortfahren ist ein aktives Abonnement erforderlich.',
  'common.insufficientPixels': 'Nicht genügend Pixel.',
  'editor.stillLoading':
    'Der Editor lädt noch. Bitte versuche es gleich erneut.',
  'publish.inProgress': 'Die Veröffentlichung läuft bereits. Bitte warte.',
  'scene.selectFirst': 'Bitte wähle zuerst eine Szene aus.',
  'scene.notFound': 'Szene in der Zeitleiste nicht gefunden.',
  'scene.required': 'Mindestens eine Szene ist erforderlich.',
  'characterVideo.maxCharactersOne': 'Du kannst höchstens 1 Figur auswählen.',
  'characterVideo.maxCharactersMany':
    'Du kannst bis zu {count} Figuren auswählen.',
  'characterVideo.characterNoImage':
    'Diese Figur hat kein Referenzbild. Füge eines im Character Studio hinzu.',
  'characterVideo.characterImageRequired':
    'Wähle zuerst ein Figurenbild aus oder lade eines hoch.',
  'characterVideo.describeCharacter':
    'Beschreibe, wie aus dem Figurenbild ein Video werden soll.',
  'characterVideo.scriptGenerated':
    'Skript erstellt. Prüfe es unten und generiere dann das Video.',
  'characterVideo.scriptPixels':
    'Dieses Skript benötigt {required} Pixel, du hast {available}.',
  'characterVideo.scriptFailed': 'Skript konnte nicht erstellt werden.',
  'characterVideo.scriptRequired':
    'Erstelle zuerst ein Skript, bevor du das Video erstellst.',
  'characterVideo.animationReady':
    'Animation bereit — klicke auf Speichern und zur Zeitleiste hinzufügen, sonst wird sie verworfen.',
  'characterVideo.videoReady':
    'Video bereit — klicke auf Speichern und zur Zeitleiste hinzufügen, sonst wird es verworfen.',
  'characterVideo.videoPixels':
    'Dieses Video benötigt {required} Pixel, du hast {available}.',
  'characterVideo.videoFailed': 'Video konnte nicht erstellt werden.',
  'characterVideo.enhanced': 'Auf 1080p verbessert.',
  'characterVideo.enhancePixels':
    'Diese Verbesserung benötigt {required} Pixel, du hast {available}.',
  'characterVideo.enhanceFailed': 'Video konnte nicht verbessert werden.',
  'characterVideo.timelineVideoRequired':
    'Erstelle zuerst ein Video, bevor du es zur Zeitleiste hinzufügst.',
  'characterVideo.trimTooShort':
    'Wähle mindestens 0,1 Sekunden zum Speichern aus.',
  'characterVideo.saveFailed':
    'Video konnte nicht gespeichert werden. Bitte versuche es erneut.',
  'characterVideo.discardVideoConfirm':
    'Dieses generierte Video geht dauerhaft verloren, wenn du fortfährst, ohne es zur Zeitleiste hinzuzufügen. Fortfahren?',
  'environment.deleted': 'Umgebung gelöscht.',
  'environment.deleteFailed': 'Umgebung konnte nicht gelöscht werden.',
  'environment.maxImages':
    'Eine Umgebung darf höchstens {max} Referenzbilder enthalten.',
  'environment.onlyFirstImages':
    'Nur die ersten {slots} Bilder wurden hinzugefügt (max. {max}).',
  'environment.unsupportedImage':
    '"{file}" ist kein unterstütztes Bild (JPG, PNG, WebP).',
  'environment.aspectRatio':
    '"{file}" hat ein Seitenverhältnis von {ratio}. Referenzbilder müssen zwischen {min} und {max} liegen. Schneide es zu und versuche es erneut.',
  'environment.uploadFailed': 'Referenzbild konnte nicht hochgeladen werden.',
  'environment.nameRequired': 'Gib der Umgebung einen Namen.',
  'environment.imageRequired': 'Lade mindestens ein Referenzbild hoch.',
  'environment.updated': '"{name}" aktualisiert.',
  'environment.created': '"{name}" erstellt.',
  'environment.saveFailed': 'Umgebung konnte nicht gespeichert werden.',
  'navigation.noRecordedAnimation':
    'Keine aufgezeichnete Animation zum Abspielen.',
  'navigation.characterUpdateUnavailable':
    'Der Figuren-Update-Handler ist nicht verfügbar.',
  'navigation.assetsOnlyAudioVideo':
    'Es können nur Audio- und Videodateien hochgeladen werden.',
  'navigation.assetsUploadFailed':
    'Ein oder mehrere Assets konnten nicht hochgeladen werden.',
  'navigation.audioPromptRequired': 'Bitte gib einen Audio-Prompt ein.',
  'navigation.audioTitleRequired': 'Bitte gib einen Audiotitel ein.',
  'navigation.audioStyleRequired': 'Bitte wähle einen Audiostil.',
  'navigation.audioGenerateFailed': 'Audio konnte nicht erzeugt werden.',
  'navigation.backgroundPromptRequired':
    'Bitte gib einen Hintergrund-Prompt ein.',
  'navigation.backgroundGenerateFailed':
    'Hintergrund konnte nicht erzeugt werden.',
  'profile.firstNameRequired': 'Der Vorname darf nicht leer sein.',
  'profile.noChanges': 'Keine Änderungen zum Speichern.',
  'profile.updated': 'Profil aktualisiert.',
  'profile.updateFailed': 'Profil konnte nicht aktualisiert werden.',
  'profile.passwordChanged': 'Passwort geändert.',
  'profile.passwordChangeFailed': 'Passwort konnte nicht geändert werden.',
  'home.audioAddFailed':
    'Audio konnte nicht zur Zeitleiste hinzugefügt werden.',
  'home.uploadedVideoAddFailed':
    'Hochgeladenes Video konnte nicht zur Zeitleiste hinzugefügt werden.',
  'home.backgroundGenerated': 'Hintergrund erfolgreich erzeugt.',
  'home.backgroundGenerateFailed': 'Bild konnte nicht erzeugt werden.',
  'home.noGeneratedAudio': 'Kein erzeugtes Audio zum Speichern.',
  'home.audioSaveStorageFailed':
    'Erzeugtes Audio konnte nicht im Speicher gespeichert werden.',
  'home.audioAdded': 'Erzeugtes Audio zur Zeitleiste hinzugefügt.',
  'home.audioSaveFailed': 'Erzeugtes Audio konnte nicht gespeichert werden.',
  'home.videoSaveFailed':
    'Erzeugtes Video konnte nicht gespeichert werden. Bitte versuche es erneut.',
  'billing.cancellationScheduled': 'Kündigung geplant — aktiv bis {date}',
};

const pt: Record<ToastMessageKey, string> = {
  'common.loginRequired': 'Faça login para continuar.',
  'common.authTokenMissing': 'Token de autenticação não encontrado.',
  'common.videoIdMissing': 'ID do vídeo não encontrado.',
  'common.imageTypeUnsupported': 'Envie uma imagem JPG, PNG ou WebP.',
  'common.imageDimensionsUnreadable':
    'Não foi possível ler as dimensões da imagem. Tente outra imagem.',
  'common.subscriptionRequired':
    'É necessário uma assinatura ativa para continuar.',
  'common.insufficientPixels': 'Pixels insuficientes.',
  'editor.stillLoading':
    'O editor ainda está carregando. Tente novamente em instantes.',
  'publish.inProgress': 'A publicação já está em andamento. Aguarde.',
  'scene.selectFirst': 'Selecione uma cena primeiro.',
  'scene.notFound': 'Cena não encontrada na linha do tempo.',
  'scene.required': 'É necessária pelo menos uma cena.',
  'characterVideo.maxCharactersOne':
    'Você pode selecionar no máximo 1 personagem.',
  'characterVideo.maxCharactersMany':
    'Você pode selecionar até {count} personagens.',
  'characterVideo.characterNoImage':
    'Este personagem não tem imagem de referência. Adicione uma no Character Studio.',
  'characterVideo.characterImageRequired':
    'Selecione ou envie uma imagem de personagem primeiro.',
  'characterVideo.describeCharacter':
    'Descreva como a imagem do personagem deve se tornar um vídeo.',
  'characterVideo.scriptGenerated':
    'Roteiro gerado. Revise abaixo e depois gere o vídeo.',
  'characterVideo.scriptPixels':
    'Este roteiro precisa de {required} pixels; você tem {available}.',
  'characterVideo.scriptFailed': 'Falha ao gerar o roteiro.',
  'characterVideo.scriptRequired':
    'Gere um roteiro primeiro antes de criar o vídeo.',
  'characterVideo.animationReady':
    'Animação pronta — clique em Salvar e adicionar à linha do tempo para manter; caso contrário, será descartada.',
  'characterVideo.videoReady':
    'Vídeo pronto — clique em Salvar e adicionar à linha do tempo para manter; caso contrário, será descartado.',
  'characterVideo.videoPixels':
    'Este vídeo precisa de {required} pixels; você tem {available}.',
  'characterVideo.videoFailed': 'Falha ao gerar o vídeo.',
  'characterVideo.enhanced': 'Aprimorado para 1080p.',
  'characterVideo.enhancePixels':
    'Este aprimoramento precisa de {required} pixels; você tem {available}.',
  'characterVideo.enhanceFailed': 'Falha ao aprimorar o vídeo.',
  'characterVideo.timelineVideoRequired':
    'Gere um vídeo antes de adicioná-lo à linha do tempo.',
  'characterVideo.trimTooShort':
    'Selecione pelo menos 0,1 segundos para salvar.',
  'characterVideo.saveFailed': 'Falha ao salvar o vídeo. Tente novamente.',
  'characterVideo.discardVideoConfirm':
    'Este vídeo gerado será perdido para sempre se você continuar sem salvá-lo na linha do tempo. Continuar?',
  'environment.deleted': 'Ambiente excluído.',
  'environment.deleteFailed': 'Falha ao excluir o ambiente.',
  'environment.maxImages':
    'Um ambiente pode ter no máximo {max} imagens de referência.',
  'environment.onlyFirstImages':
    'Apenas as primeiras {slots} imagens foram adicionadas (máx. {max}).',
  'environment.unsupportedImage':
    '"{file}" não é uma imagem compatível (JPG, PNG, WebP).',
  'environment.aspectRatio':
    '"{file}" tem proporção de {ratio}. As imagens de referência devem estar entre {min} e {max}. Recorte-a e tente novamente.',
  'environment.uploadFailed': 'Falha ao enviar a imagem de referência.',
  'environment.nameRequired': 'Dê um nome ao ambiente.',
  'environment.imageRequired': 'Envie pelo menos uma imagem de referência.',
  'environment.updated': '"{name}" atualizado.',
  'environment.created': '"{name}" criado.',
  'environment.saveFailed': 'Falha ao salvar o ambiente.',
  'navigation.noRecordedAnimation': 'Nenhuma animação gravada para reproduzir.',
  'navigation.characterUpdateUnavailable':
    'O atualizador de personagens não está disponível.',
  'navigation.assetsOnlyAudioVideo':
    'Apenas arquivos de áudio e vídeo podem ser enviados.',
  'navigation.assetsUploadFailed': 'Um ou mais recursos falharam ao enviar.',
  'navigation.audioPromptRequired': 'Digite um prompt de áudio.',
  'navigation.audioTitleRequired': 'Digite um título de áudio.',
  'navigation.audioStyleRequired': 'Selecione um estilo de áudio.',
  'navigation.audioGenerateFailed': 'Falha ao gerar áudio.',
  'navigation.backgroundPromptRequired': 'Digite um prompt de fundo.',
  'navigation.backgroundGenerateFailed': 'Falha ao gerar o fundo.',
  'profile.firstNameRequired': 'O nome não pode ficar vazio.',
  'profile.noChanges': 'Nenhuma alteração para salvar.',
  'profile.updated': 'Perfil atualizado.',
  'profile.updateFailed': 'Falha ao atualizar o perfil.',
  'profile.passwordChanged': 'Senha alterada.',
  'profile.passwordChangeFailed': 'Falha ao alterar a senha.',
  'home.audioAddFailed': 'Falha ao adicionar áudio à linha do tempo.',
  'home.uploadedVideoAddFailed':
    'Falha ao adicionar o vídeo enviado à linha do tempo.',
  'home.backgroundGenerated': 'Fundo gerado com sucesso.',
  'home.backgroundGenerateFailed': 'Falha ao gerar a imagem.',
  'home.noGeneratedAudio': 'Nenhum áudio gerado para salvar.',
  'home.audioSaveStorageFailed':
    'Falha ao salvar o áudio gerado no armazenamento.',
  'home.audioAdded': 'Áudio gerado adicionado à linha do tempo.',
  'home.audioSaveFailed': 'Falha ao salvar o áudio gerado.',
  'home.videoSaveFailed': 'Falha ao salvar o vídeo gerado. Tente novamente.',
  'billing.cancellationScheduled': 'Cancelamento agendado — ativo até {date}',
};

const ja: Record<ToastMessageKey, string> = {
  'common.loginRequired': '続行するにはログインしてください。',
  'common.authTokenMissing': '認証トークンが見つかりません。',
  'common.videoIdMissing': '動画IDが見つかりません。',
  'common.imageTypeUnsupported':
    'JPG、PNG、またはWebP画像をアップロードしてください。',
  'common.imageDimensionsUnreadable':
    '画像のサイズを読み取れませんでした。別の画像を試してください。',
  'common.subscriptionRequired':
    '続行するには有効なサブスクリプションが必要です。',
  'common.insufficientPixels': 'ピクセルが不足しています。',
  'editor.stillLoading':
    'エディターはまだ読み込み中です。しばらくしてからもう一度お試しください。',
  'publish.inProgress': '公開処理はすでに進行中です。お待ちください。',
  'scene.selectFirst': '先にシーンを選択してください。',
  'scene.notFound': 'タイムラインにシーンが見つかりません。',
  'scene.required': '少なくとも1つのシーンが必要です。',
  'characterVideo.maxCharactersOne': '選択できるキャラクターは1体までです。',
  'characterVideo.maxCharactersMany':
    '選択できるキャラクターは{count}体までです。',
  'characterVideo.characterNoImage':
    'このキャラクターには参照画像がありません。Character Studioで追加してください。',
  'characterVideo.characterImageRequired':
    '先にキャラクター画像を選択またはアップロードしてください。',
  'characterVideo.describeCharacter':
    'キャラクター画像をどのように動画にするか説明してください。',
  'characterVideo.scriptGenerated':
    'スクリプトを生成しました。下で確認してから動画を生成してください。',
  'characterVideo.scriptPixels':
    'このスクリプトには{required}ピクセル必要です。現在{available}あります。',
  'characterVideo.scriptFailed': 'スクリプトの生成に失敗しました。',
  'characterVideo.scriptRequired':
    '動画を作成する前に、先にスクリプトを生成してください。',
  'characterVideo.animationReady':
    'アニメーションの準備ができました — 保持するには「保存してタイムラインに追加」を押してください。押さない場合は破棄されます。',
  'characterVideo.videoReady':
    '動画の準備ができました — 保持するには「保存してタイムラインに追加」を押してください。押さない場合は破棄されます。',
  'characterVideo.videoPixels':
    'この動画には{required}ピクセル必要です。現在{available}あります。',
  'characterVideo.videoFailed': '動画の生成に失敗しました。',
  'characterVideo.enhanced': '1080pに高画質化しました。',
  'characterVideo.enhancePixels':
    'この高画質化には{required}ピクセル必要です。現在{available}あります。',
  'characterVideo.enhanceFailed': '動画の高画質化に失敗しました。',
  'characterVideo.timelineVideoRequired':
    'タイムラインに追加する前に動画を生成してください。',
  'characterVideo.trimTooShort': '保存するには0.1秒以上選択してください。',
  'characterVideo.saveFailed':
    '動画の保存に失敗しました。もう一度お試しください。',
  'characterVideo.discardVideoConfirm':
    'この生成済み動画は、タイムラインに保存せずに続行すると完全に失われます。続行しますか？',
  'environment.deleted': '環境を削除しました。',
  'environment.deleteFailed': '環境の削除に失敗しました。',
  'environment.maxImages': '1つの環境には最大{max}枚の参照画像を設定できます。',
  'environment.onlyFirstImages':
    '最初の{slots}枚のみ追加されました（最大{max}枚）。',
  'environment.unsupportedImage':
    '「{file}」は対応していない画像です（JPG、PNG、WebP）。',
  'environment.aspectRatio':
    '「{file}」のアスペクト比は{ratio}です。参照画像は{min}～{max}の範囲にしてください。切り抜いて再試行してください。',
  'environment.uploadFailed': '参照画像のアップロードに失敗しました。',
  'environment.nameRequired': '環境に名前を付けてください。',
  'environment.imageRequired':
    '参照画像を少なくとも1枚アップロードしてください。',
  'environment.updated': '「{name}」を更新しました。',
  'environment.created': '「{name}」を作成しました。',
  'environment.saveFailed': '環境の保存に失敗しました。',
  'navigation.noRecordedAnimation':
    '再生できる録画アニメーションがありません。',
  'navigation.characterUpdateUnavailable':
    'キャラクター更新機能を利用できません。',
  'navigation.assetsOnlyAudioVideo':
    'アップロードできるのは音声ファイルと動画ファイルのみです。',
  'navigation.assetsUploadFailed':
    '1つ以上のアセットのアップロードに失敗しました。',
  'navigation.audioPromptRequired': '音声プロンプトを入力してください。',
  'navigation.audioTitleRequired': '音声タイトルを入力してください。',
  'navigation.audioStyleRequired': '音声スタイルを選択してください。',
  'navigation.audioGenerateFailed': '音声の生成に失敗しました。',
  'navigation.backgroundPromptRequired': '背景プロンプトを入力してください。',
  'navigation.backgroundGenerateFailed': '背景の生成に失敗しました。',
  'profile.firstNameRequired': '名前を空にすることはできません。',
  'profile.noChanges': '保存する変更はありません。',
  'profile.updated': 'プロフィールを更新しました。',
  'profile.updateFailed': 'プロフィールの更新に失敗しました。',
  'profile.passwordChanged': 'パスワードを変更しました。',
  'profile.passwordChangeFailed': 'パスワードの変更に失敗しました。',
  'home.audioAddFailed': '音声をタイムラインに追加できませんでした。',
  'home.uploadedVideoAddFailed':
    'アップロードした動画をタイムラインに追加できませんでした。',
  'home.backgroundGenerated': '背景を生成しました。',
  'home.backgroundGenerateFailed': '画像の生成に失敗しました。',
  'home.noGeneratedAudio': '保存できる生成音声がありません。',
  'home.audioSaveStorageFailed': '生成音声をストレージに保存できませんでした。',
  'home.audioAdded': '生成音声をタイムラインに追加しました。',
  'home.audioSaveFailed': '生成音声の保存に失敗しました。',
  'home.videoSaveFailed':
    '生成した動画の保存に失敗しました。もう一度お試しください。',
  'billing.cancellationScheduled': 'キャンセルを予定しました — {date}まで有効',
};

const MESSAGES: Record<AppLanguage, Record<ToastMessageKey, string>> = {
  en,
  zh,
  es,
  fr,
  de,
  pt,
  ja,
};

export function toastMessage(
  key: ToastMessageKey,
  vars: ToastMessageVars = {},
  language: AppLanguage = detectLanguage()
): string {
  const template = MESSAGES[language]?.[key] ?? en[key];
  return template.replace(/\{(\w+)\}/g, (_, name: string) => {
    const value = vars[name];
    if (typeof value === 'number' && Number.isFinite(value)) {
      return new Intl.NumberFormat(language, {
        maximumFractionDigits: 3,
      }).format(value);
    }
    return String(value ?? '');
  });
}
